import React from 'react';
import { CheckCircle, Clock, Database, Download, FileText, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { TaskStatus } from '@/types/productFeed';
import SubTask from '@/components/SubTask';
import DownloadReportField from '@/components/DownloadReportField';
import { subTaskNameConstants } from '@/lib/constants/subTaskNameConstants';

interface Step2CompletionProps {
  vendor: string;
  vendorStorageLocationType: string;
  nestingaleS3Location: string;
  imageNamingSyntax: string;
  dropboxFolderStructureType: string;
  imageColumnName: string;
  searchImagesPattern: string;
  onPopulateMasterData: () => void;
  stepTwoTasks?: TaskStatus;
  buttonText?: string;
}


const Step2Completion: React.FC<Step2CompletionProps> = ({
  vendor,
  vendorStorageLocationType,
  nestingaleS3Location,
  imageNamingSyntax,
  dropboxFolderStructureType,
  imageColumnName,
  searchImagesPattern,
  onPopulateMasterData,
  stepTwoTasks,
  buttonText = "Populate Master data"
}) => {
  const subTasks = stepTwoTasks?.subTasks || [];
  const rawFeedReportGeneratedTask = subTasks.find(task => task.task === subTaskNameConstants?.rawFeedImgProcessReportGenerate);
  return (
    <div className="space-y-6">
      {/* Sub Tasks Section */}
      <SubTask subTasks={subTasks} />

      {/* Status Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Image Processing */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium">Image Processing</h3>
            </div>

            {/* Download Report Field */}
            <DownloadReportField
              status={rawFeedReportGeneratedTask?.status || 'pending'}
              rawProductFeedValidationReportLocation={rawFeedReportGeneratedTask?.failedImagesReportUrl || ""}
            />

            {/* Populate Master Data Button */}
            <Button
              // disabled={!subTasks?.length || !subTasks.every(task => task?.status === "completed") || stepTwoTasks?.status !== "completed"}
              disabled={!subTasks?.length || !subTasks.every(task => task?.status === "completed")}
              onClick={onPopulateMasterData}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white p-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {buttonText}
            </Button>
          </div>

          <Separator />

          {/* Image Configs */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium">Image Configs</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Vendor:</span>
                <span className="text-sm font-medium">{vendor}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Vendor Storage Location Type:</span>
                <span className="text-sm font-medium">{vendorStorageLocationType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Nestingale S3 Location:</span>
                <div className="flex items-center space-x-1">
                  <FileText className="h-3 w-3 text-gray-400" />
                  <span className="text-sm font-medium max-w-xs truncate">{nestingaleS3Location}</span>
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Image Naming Syntax:</span>
                <span className="text-sm font-medium">{imageNamingSyntax}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Dropbox Folder Structure Type:</span>
                <span className="text-sm font-medium">{dropboxFolderStructureType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Image Column Name:</span>
                <span className="text-sm font-medium">{imageColumnName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Search Images Pattern:</span>
                <span className="text-sm font-medium">{searchImagesPattern}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">status:</span>
                <span className="text-sm font-medium">{stepTwoTasks?.status || 'pending'}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Step2Completion; 